
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { getSMSLogs } from "@/utils/smsUtils";
import { Lead } from "@/context/LeadContext";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { MessageSquare, CheckCircle2, XCircle, MousePointerClick } from "lucide-react";

interface PartnerSMSMetricsProps {
  partnerId: string;
  leads: Lead[];
}

const PartnerSMSMetrics: React.FC<PartnerSMSMetricsProps> = ({ partnerId, leads }) => {
  const [smsLogs, setSmsLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSMSLogs = async () => {
      try {
        setIsLoading(true);
        const logs = await getSMSLogs(partnerId);
        setSmsLogs(logs);
      } catch (error) {
        console.error("Error fetching SMS logs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (partnerId) fetchSMSLogs();
  }, [partnerId]);

  const sends = smsLogs.filter((l) => l.type === "send");
  const clicks = smsLogs.filter((l) => l.type === "click");

  const delivered = sends.filter(
    (l) => l.status === "delivered" || l.status === "sent"
  ).length;
  const failed = sends.filter(
    (l) => l.status === "failed" || l.status === "error"
  ).length;
  const deliveryRate =
    sends.length > 0 ? Math.round((delivered / sends.length) * 100) : 0;

  // Build per-lead summary: one row per lead that received an SMS
  const leadMap = new Map<string, { send?: any; click?: any }>();
  sends.forEach((s) => {
    if (!s.leadId) return;
    if (!leadMap.has(s.leadId)) leadMap.set(s.leadId, {});
    leadMap.get(s.leadId)!.send = s;
  });
  clicks.forEach((c) => {
    if (!c.leadId) return;
    if (!leadMap.has(c.leadId)) leadMap.set(c.leadId, {});
    if (!leadMap.get(c.leadId)!.click) leadMap.get(c.leadId)!.click = c;
  });

  const leadSummary = Array.from(leadMap.entries())
    .map(([leadId, data]) => ({ leadId, ...data }))
    .sort((a, b) => {
      const at = new Date(
        a.send?.timestamp || a.click?.timestamp || 0
      ).getTime();
      const bt = new Date(
        b.send?.timestamp || b.click?.timestamp || 0
      ).getTime();
      return bt - at;
    });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-blue-500" />
              <div className="text-2xl font-bold">{sends.length}</div>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Απεσταλμένα</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <div className="text-2xl font-bold">{delivered}</div>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Παραδόθηκαν</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <div className="text-2xl font-bold">{failed}</div>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Απέτυχαν</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <MousePointerClick className="h-4 w-4 text-purple-500" />
              <div className="text-2xl font-bold">{clicks.length}</div>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Κλικ</p>
          </CardContent>
        </Card>
      </div>

      {/* Delivery rate bar */}
      {sends.length > 0 && (
        <Card>
          <CardContent className="pt-6 pb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Ποσοστό Παράδοσης</span>
              <span className="text-sm font-bold">{deliveryRate}%</span>
            </div>
            <Progress value={deliveryRate} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Per-lead table */}
      <Card>
        <CardHeader>
          <CardTitle>Ανά Επαφή</CardTitle>
          <CardDescription>
            Κατάσταση SMS για κάθε lead που ανατέθηκε στον συνεργάτη
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leadSummary.length > 0 ? (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Επαφή
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Αποστολή
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Κατάσταση
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Κλικ
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leadSummary.map(({ leadId, send, click }) => {
                    const lead = leads.find((l) => l.id === leadId);
                    const name =
                      lead?.name ||
                      send?.leadName ||
                      click?.leadName ||
                      leadId.slice(0, 8) + "…";
                    const phone =
                      lead?.phone || send?.leadPhone || click?.leadPhone || "—";
                    return (
                      <tr key={leadId} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-3">
                          <div className="font-medium">{name}</div>
                          <div className="text-xs text-muted-foreground">
                            {phone}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-xs text-muted-foreground">
                          {send?.timestamp
                            ? format(
                                new Date(send.timestamp),
                                "dd/MM/yy HH:mm",
                                { locale: el }
                              )
                            : "—"}
                        </td>
                        <td className="py-2 px-3">
                          {send ? (
                            send.status === "delivered" ||
                            send.status === "sent" ? (
                              <Badge
                                variant="outline"
                                className="bg-green-50 text-green-700 border-green-200"
                              >
                                Παραδόθηκε
                              </Badge>
                            ) : send.status === "failed" ||
                              send.status === "error" ? (
                              <Badge
                                variant="outline"
                                className="bg-red-50 text-red-700 border-red-200"
                              >
                                Απέτυχε
                              </Badge>
                            ) : (
                              <Badge variant="outline">{send.status}</Badge>
                            )
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {click ? (
                            <div>
                              <Badge
                                variant="outline"
                                className="bg-purple-50 text-purple-700 border-purple-200"
                              >
                                Ναι
                              </Badge>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {format(
                                  new Date(click.timestamp),
                                  "dd/MM HH:mm",
                                  { locale: el }
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">
                              Όχι
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Δεν υπάρχουν καταγεγραμμένα SMS
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerSMSMetrics;
